import { toast } from 'sonner';
import { useEffect, useState } from 'react';
import { Loader2, Save, Trash2 } from 'lucide-react';
import { useNavigate, useParams } from '@tanstack/react-router';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { P, PermissionGuard, RequirePermission } from '@/features/permissions';
import { SchemaFieldBuilder } from '../components/SchemaFieldBuilder';
import { usePageHeaderSetter } from '@/components/layout/PageHeaderContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  useDeleteTemplate,
  useTemplate,
  useUpdateTemplate,
} from '../api/templates-api';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

export function TemplateDetailPage() {
  return (
    <PermissionGuard permission={P.READ_TEMPLATE}>
      <TemplateDetailPageContent />
    </PermissionGuard>
  );
}

function TemplateDetailPageContent() {
  const params = useParams({ strict: false }) as { templateId: string };
  const navigate = useNavigate();
  const setPageHeader = usePageHeaderSetter();

  const { isLoading, data: template } = useTemplate(params.templateId);
  const updateTemplate = useUpdateTemplate();
  const deleteTemplate = useDeleteTemplate();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [instructions, setInstructions] = useState('');
  const [jsonSchema, setJsonSchema] = useState<Record<string, unknown>>({});
  const [isActive, setIsActive] = useState(true);
  const [enableTagging, setEnableTagging] = useState(false);
  const [taggingInstructions, setTaggingInstructions] = useState('');

  useEffect(() => {
    setPageHeader({
      title: template?.name ?? 'Template Detail',
      actions: (
        <div className="flex gap-2">
          <RequirePermission permission={P.DELETE_TEMPLATE}>
            <Dialog>
              <DialogTrigger asChild>
                <Button size="sm" variant="destructive">
                  <Trash2 className="h-4 w-4" />
                  <span className="hidden md:inline">Delete</span>
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Delete Template</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-muted-foreground">
                  Are you sure you want to delete this template? This action cannot be undone.
                </p>
                <DialogFooter>
                  <DialogClose asChild>
                    <Button variant="outline">Cancel</Button>
                  </DialogClose>
                  <Button
                    variant="destructive"
                    disabled={deleteTemplate.isPending}
                    onClick={handleDelete}
                  >
                    {deleteTemplate.isPending && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Delete
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </RequirePermission>
          <RequirePermission permission={P.UPDATE_TEMPLATE}>
            <Button size="sm" disabled={updateTemplate.isPending} onClick={handleSave}>
              {updateTemplate.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              <span className="hidden md:inline">Save</span>
            </Button>
          </RequirePermission>
        </div>
      ),
    });
    return () => setPageHeader(null);
  }, [setPageHeader, template?.name, updateTemplate.isPending, deleteTemplate.isPending]);

  useEffect(() => {
    if (template) {
      setName(template.name);
      setDescription(template.description ?? '');
      setSystemPrompt(template.system_prompt ?? '');
      setInstructions(template.instructions);
      setJsonSchema(template.json_schema ?? {});
      setIsActive(template.is_active);
      setEnableTagging(template.enable_tagging);
      setTaggingInstructions(template.tagging_instructions ?? '');
    }
  }, [template]);

  async function handleSave() {
    if (!name.trim()) return;
    if (!instructions.trim()) return;

    try {
      await updateTemplate.mutateAsync({
        name: name.trim(),
        is_active: isActive,
        id: params.templateId,
        json_schema: jsonSchema,
        instructions: instructions.trim(),
        description: description.trim() || null,
        system_prompt: systemPrompt.trim() || null,
        enable_tagging: enableTagging,
        tagging_instructions: taggingInstructions.trim() || null,
      });
      toast.success('Template updated successfully');
    } catch {
      toast.error('Failed to update template');
    }
  }

  async function handleDelete() {
    try {
      await deleteTemplate.mutateAsync(params.templateId);
      toast.success('Template deleted successfully');
      navigate({ to: '/templates' });
    } catch {
      toast.error('Failed to delete template');
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!template) {
    return (
      <div className="flex items-center justify-center p-12">
        <p className="text-muted-foreground">Template not found.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 p-4 md:p-6">
      <Card>
        <CardHeader>
          <CardTitle>Template Details</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="detail-name">Name</Label>
            <Input
              id="detail-name"
              value={name}
              placeholder="Template name"
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="detail-description">Description</Label>
            <Textarea
              id="detail-description"
              rows={2}
              value={description}
              placeholder="Template description"
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="flex items-center gap-2">
            <Label htmlFor="detail-active">Active</Label>
            <input
              id="detail-active"
              type="checkbox"
              checked={isActive}
              className="h-4 w-4"
              onChange={(e) => setIsActive(e.target.checked)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Prompts</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="detail-system-prompt">System Prompt</Label>
            <Textarea
              id="detail-system-prompt"
              rows={4}
              value={systemPrompt}
              placeholder="System prompt for the AI"
              onChange={(e) => setSystemPrompt(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="detail-instructions">Instructions</Label>
            <Textarea
              id="detail-instructions"
              rows={4}
              value={instructions}
              placeholder="Instructions for generating the report"
              onChange={(e) => setInstructions(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Clinical Tagging</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex items-center space-x-2">
            <Switch
              id="enable_tagging"
              checked={enableTagging}
              onCheckedChange={setEnableTagging}
            />
            <Label htmlFor="enable_tagging">Enable Clinical Tagging</Label>
          </div>

          {enableTagging && (
            <div className="flex flex-col gap-2">
              <Label htmlFor="tagging-instructions">Tagging Instructions (Optional)</Label>
              <Textarea
                id="tagging-instructions"
                rows={4}
                placeholder="Custom instructions for the clinical tagging AI..."
                value={taggingInstructions}
                onChange={(e) => setTaggingInstructions(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Leave blank to use the default tagging prompt.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Context Fields</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="mb-3 text-sm text-muted-foreground">
            Define the fields that appear when this template is used in an encounter.
          </p>
          <SchemaFieldBuilder value={jsonSchema} onChange={setJsonSchema} />
        </CardContent>
      </Card>

    </div>
  );
}
